'use strict';

class SigninController {
  constructor($log,
              $rootScope,
              $scope,
              $filter,
              $interval,
              $timeout,
              $translate,
              utils,
              $state,
              config,
              CloudConfigService,
              AuthService,
              UsersService,
              I18nService,
              PermissionsService,
              ToastsService) {

    // #region Define fields

    const $t = $filter('translate');
    let secondsUntilNextPasscodeSend = 120;
    const CURRENT_ADMIN_LOCAL_STORAGE_KEY = 'App-Admin';
    const AVAILABLE_CLOUDS_LOCAL_STORAGE_KEY = 'App-Clouds';
    const CURRENT_SECOND_FACTOR_TYPE = 'Main-Factor';
    let codeCountdownInterval;

    $scope.hideHint = false;
    $scope.availableLanguages = [];
    $scope.username = $state.params && $state.params.email;
    $scope.externalMsg = $rootScope.errorMsg;
    $rootScope.errorMsg = void 0;
    $scope.loading = false;

    $scope.authMethods = [
      {
        'id': 2,
        'name': $t('signin.methods.email.title'),
        'desc': $t('signin.methods.email.desc')
      },
      {
        'id': 3,
        'name': $t('signin.methods.sms.title'),
        'desc': $t('signin.methods.sms.desc')
      },
      {
        'id': 4,
        'name': $t('signin.methods.totp.title'),
        'desc': $t('signin.methods.totp.desc')
      }
    ];

    /**
     * Signin UI state (step)
     * @type {'password'|'method'|'passcode'|'totp'}
     */
    $scope.state = 'password';

    /**
     * 2FA types for confirmation: 2 - email, 3 - sms, 4 - toptp
     * (default pre-selected type is email)
     */
    $scope.factorType = parseInt(utils.getStorageItem(CURRENT_SECOND_FACTOR_TYPE)) || 2;

    const smsEnabled = process?.env?.['SMS_ENABLED'];
    if (!smsEnabled || smsEnabled !== 'true') {
      $scope.authMethods.splice(1, 1);
      if ($scope.factorType === 3) {
        $scope.factorType = 2;
      }
    }

    // Hide cloud dropdown depends on server config
    const maestroLocal = window.location?.hostname.indexOf('localhost') === 0;
    const showCloudDropdown = !!config.getConfigByKey('environment').showCloudDropdown;
    $scope.showClouds = maestroLocal && showCloudDropdown;

    // #endregion

    // #region Put on scope only the stuff we really need there
    angular.extend($scope, {
      onLanguageDropdownSelectionChange: onLanguageDropdownSelectionChange,
      keyPressSignin: keyPressSignin,
      keyPressPasscode: keyPressPasscode,
      signin: signin,
      submitPasscode: submitPasscode,
      resendCode: resendCode,
      cancelCodeCountdown: cancelCodeCountdown,
      gotoMethod: gotoMethod,
      message: '',
      countdown: 0
    });
    // #endregion

    initPage();

    // #region Methods
    function getConnectSettings() {
      return CloudConfigService.getClouds()
        .then(clouds => {
          $scope.clouds = clouds || [];
          // Display cloud selector for non-Production host
          // NOTE: First cloud in the array is current cloud connected
          $scope.showClouds = $scope.showClouds ? true : $scope.clouds[0] && $scope.clouds[0].type === 1;
          // Save clouds list for future
          utils.setStorageItem(AVAILABLE_CLOUDS_LOCAL_STORAGE_KEY, clouds);
        })
        .then(() => CloudConfigService.getCurrentCloud())
        .then(currentCloud => {
          // Find out index of the current cloud and select it in the clouds dropdown
          $scope.selectedCloudIndex = $scope.clouds.findIndex(c => c.name === currentCloud.name);
          $scope.selectedCloud = $scope.clouds[$scope.selectedCloudIndex];
          // Update the dropdown state
          $('#drop-clouds').dropdown('set selected', $scope.selectedCloudIndex.toString());
        })
        .catch(err => handleError(err));
    }

    function initUserInfo() {
      return UsersService.getCurrentUserInfo()
        .then(data => {
          $scope.state = 'password';
          $scope.userInfo = data;
          // For other legacy parts of Maestro to know the current user
          utils.setStorageItem(CURRENT_ADMIN_LOCAL_STORAGE_KEY, $scope.userInfo);
          // Save preferred 2FA method
          utils.setStorageItem(CURRENT_SECOND_FACTOR_TYPE, $scope.factorType);

          // See if the user have enough permissions to use Maestro at all. If not - log him back out
          if (!PermissionsService.canIUseMaestro()) {
            handleError({
              resultCode: 7,
              resultCodeDesc: $t('errors.noMaestroAccess')
            });
          } else {
            // Now we safe to navigate to the dashboard and it will not be empty for the user
            $state.go('main');
          }
        });
    }

    function handleError(error) {
      $scope.loading = false;

      if (error?.resultCode === 44) { // Too often API calls
        ToastsService.error($t('errors.tooManyApiCalls'), $t('errors.tooManyApiCallsDesc'));
      } else {
        utils.handleApiError(error);
      }

      return AuthService.logoutFromThisBrowser();
    }

    function checkLoginStatus() {
      const result = AuthService.isAuthenticated();
      result && initUserInfo();
      return result;
    }

    function initPage() {
      initLanguage();

      $('#drop-clouds').dropdown({
        onChange: function(value, text) {
          $scope.$applyAsync(() => {
            $scope.selectedCloudIndex = value;
            $scope.selectedCloud = $scope.clouds[$scope.selectedCloudIndex];
            CloudConfigService.setCurrentCloud(angular.fromJson(angular.toJson($scope.selectedCloud)));
          });
        }
      });

      $('#login-form').form({
        inline: true,
        fields: {
          username: {
            identifier: 'username',
            rules: [
              {
                type: 'empty',
                prompt: $t('prompt.username')
              }
            ]
          },
          password: {
            identifier: 'password',
            rules: [
              {
                type: 'empty',
                prompt: $t('prompt.password.empty')
              }
            ]
          }
        }
      });

      $('#passcode-form').form({
        inline: true,
        fields: {
          passcode: {
            identifier: 'passcode',
            rules: [
              {
                type: 'empty',
                prompt: $t('prompt.passcode.enter')
              },
              {
                type: 'regExp[/^[A-Za-z0-9]{6}$/gi]',
                prompt: $t('prompt.passcode.invalid')
              }
            ]
          }
        }
      });

      $('#totp-form').form({
        inline: true,
        fields: {
          totp: {
            identifier: 'totp',
            rules: [
              {
                type: 'empty',
                prompt: $t('prompt.totp.enter')
              },
              {
                type: 'regExp[/^[0-9]{6}$/gi]',
                prompt: $t('prompt.totp.invalid')
              }
            ]
          }
        }
      });

      if (checkLoginStatus()) {
        return;
      }

      getConnectSettings();
      getPasscodeTimeout();
    }

    function getPasscodeTimeout() {
      return AuthService.getPasscodeResendTimeout()
        .then((timeout) => {
          secondsUntilNextPasscodeSend = timeout;
        }, (err) => utils.handleApiError(err));
    }

    function initLanguage() {
      $scope.availableLanguages = I18nService.getLocalesList();
      $scope.selectedLng = I18nService.getLocale();
      $scope.selectedLngIndex = $scope.availableLanguages.findIndex(l => l.code === $scope.selectedLng.code);

      $('#drop-lang').dropdown({
        onChange: function(value, text) {
          $scope.$applyAsync(() => {
            $scope.selectedLngIndex = value;
            let lng = $scope.availableLanguages[value];
            onLanguageDropdownSelectionChange(lng);
          });
        }
      });
    }

    function onLanguageDropdownSelectionChange(lng) {
      I18nService.setLocale(lng);
      $scope.selectedLng = I18nService.getLocale();
    }

    function keyPressSignin($event) {
      if ($event.keyCode === 13) {
        $scope.hideHint = true;
        $scope.signin();
      }
    }

    function keyPressPasscode($event) {
      if ($event.keyCode === 13) {
        $scope.hideHint = true;
        $scope.submitPasscode();
      }
    }

    /**
     * Sign in to system with password
     */
    function signin() {
      // Prevent doubling of the auth services trigger
      if ($scope.loading) {
        return;
      }
      // Hide the automatic logout message if it exists
      if ($scope.externalMsg) {
        $scope.externalMsg = undefined;
      }
      let formElement = $scope.state === 'method' ? $('#method-form') : $('#login-form');
      let isValid = formElement.form('is valid');
      if (!isValid) {
        return;
      }

      $scope.loading = true;
      let password = $scope.password;
      let passcode = void 0;

      AuthService.logoutFromThisBrowser()
        .then(() => AuthService.login($scope.username, password, passcode, true, $scope.factorType))
        .then(() => initUserInfo())
        .catch(err => {
          if (err.resultCode === 17) { // Proceed to 2-factor step
            $scope.state = 'passcode';
            if ($scope.factorType === 4) {
              // NOTE: No TOTP secrets are linked to account, so server sent SMS
              $scope.message = $t('prompt.totp.smsInsteadOfTotp') + ' ' + (err.resultCodeMessage || err.resultCodeDesc);
            } else {
              $scope.message = err.resultCodeMessage || err.resultCodeDesc;
            }
            $scope.factorType = err.passcodeDeliveryType || 2; // Fallback to Email
            startCodeCountdown();
          } else if (err.resultCode === 24 && $scope.factorType === 4) {
            $scope.message = undefined;
            $scope.state = 'totp';
            inputFocus('totp');
            $scope.loading = false;
          } else {
            handleError(err);
          }
        });
    }

    /**
     * Submit 2FA with passcode
     */
    function submitPasscode() {
      if ($scope.loading) {
        return;
      }

      let formElement = $scope.state === 'passcode' ? $('#passcode-form') : $('#totp-form');
      let isValid = formElement.form('is valid');
      if (!isValid) {
        return;
      }

      $scope.loading = true;
      let password = $scope.password;
      let passcode = void 0;

      // Check if that is 2-factor call
      if ($scope.state === 'passcode') {
        password = void 0;
        passcode = $scope.passcode.toUpperCase();
      } else if ($scope.state === 'totp') {
        passcode = $scope.totp;
      }

      AuthService.login($scope.username, password, passcode, true, $scope.factorType)
        .then(() => initUserInfo())
        .catch(err => {
          if (err.resultCode === 35) { // Phone is missing at 2-factor
            $scope.loading = false;
            $scope.hideHint = false;
            $scope.externalMsg = $t('errors.twoFactorRequired');
          } else if (err.resultCode === 12 && $scope.state === 'passcode') { // Passcode is wrong
            $scope.loading = false;
            ToastsService.error($t('errors.wrongPasscode'), $t('errors.wrongPasscodeDesc'));
          } else if (err.resultCode === 12 && $scope.state === 'totp') { // OTP is wrong
            $scope.loading = false;
            ToastsService.error($t('errors.wrongOtpCode'), $t('errors.wrongOtpCodeDesc'));
          } else {
            handleError(err);
          }
        });
    }

    function startCodeCountdown() {
      $scope.loading = false;
      inputFocus('passcode');

      $scope.countdown = secondsUntilNextPasscodeSend;
      if (codeCountdownInterval) {
        $interval.cancel(codeCountdownInterval);
      }
      codeCountdownInterval = $interval(() => {
        if ($scope.countdown === 0) {
          $interval.cancel(codeCountdownInterval);
          codeCountdownInterval = void 0;
          return;
        }
        $scope.countdown = $scope.countdown - 1;
      }, 1000);
    }

    function cancelCodeCountdown() {
      if ($scope.loading) {
        return;
      }
      if (codeCountdownInterval) {
        $interval.cancel(codeCountdownInterval);
        codeCountdownInterval = void 0;
      }
      $scope.state = 'password';
    }

    /**
     * Resend SMS passcode after 120 sec
     */
    function resendCode() {
      if ($scope.loading) {
        return;
      }

      $scope.loading = true;
      inputFocus('passcode');

      AuthService.login($scope.username, $scope.password, void 0, true)
        .then(() => ToastsService.success(null, $t('prompt.passcode.sent')))
        .catch(err => {
          if (err.resultCode === 17) { // Code successfully resent
            $scope.state = 'passcode';
            startCodeCountdown();
            return;
          }

          $scope.loading = false;
          $scope.state = 'password'; // Return to username/password step
          inputFocus('password');
          $scope.message = err.resultCodeMessage || err.resultCodeDesc;
          handleError(err);
        });
    }

    function gotoMethod() {
      $scope.state = 'method';
    }

    /**
     * Focus on form field input for passcode.
     * @param {text} input Form input ID: passcode, password, totp.
     */
    function inputFocus(input) {
      $scope[input] = null;
      $timeout(function() {
        $('#' + input)[0].focus();
      }, 0);
    }

    // #endregion

  }
}

export default angular
  .module('signin.controller', [])
  .controller('SigninController', SigninController)
  .name;
